import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useCart } from "../contexts/CartContext";
import { useAddress } from "../contexts/AddressContext";
import BackHeader from "../components/BackHeader";
import NavigationBar from "../components/NavigationBar";
import CartPageSkeleton from "../components/skeletons/CartPageSkeleton";
import CartItem from "../components/CartItem";
import {
  calculateOrder,
  transformCartToCalculateRequest,
  calculateOrderGift,
  transformCartToCalculateGiftRequest,
  CalculateOrderResponse,
  MIN_ORDER_AMOUNT_NOT_MET_CODE,
  SHOP_PERMANENTLY_CLOSED_CODE,
} from "../api/cartService";
import { getRestaurantDetails } from "../api/restaurantService";
import useTranslation from "../hooks/useTranslation";
import { useDebounce } from "../hooks/useDebounce";
import {
  SupportedCurrency,
  formatPrice as formatPriceWithCurrency,
  getCurrencyForCart,
  convertCurrency,
} from "../utils/currencyUtils";
import { getCurrentLanguage } from "../config/languageConfig";
import { getPromotionUserId } from "../utils/promotionUserIdUtils";
import { getPromotionResolution } from "../utils/promotionResolutionUtils";

// 시간 관련 유틸리티 함수들
const getCurrentKSTTime = () => {
  // Asia/Seoul 타임존으로 현재 시간을 생성
  const now = new Date();
  const kstTime = new Date(now.toLocaleString("en-US", {timeZone: "Asia/Seoul"}));
  return kstTime;
};

const getAvailableTimeSlots = () => {
  const currentKST = getCurrentKSTTime();
  const currentHour = currentKST.getHours();
  
  // 예약 가능 시간 정책
  let startHour = 0;
  if (currentHour >= 10 && currentHour < 11) startHour = 14;
  else if (currentHour >= 11 && currentHour < 12) startHour = 15;
  else if (currentHour >= 12 && currentHour < 13) startHour = 16;
  else if (currentHour >= 13 && currentHour < 14) startHour = 17;
  else if (currentHour >= 14 && currentHour < 15) startHour = 18;
  else if (currentHour >= 15 && currentHour < 16) startHour = 19;
  else if (currentHour >= 16 && currentHour < 17) startHour = 20;
  else if (currentHour >= 17 && currentHour < 18) startHour = 21;
  else if (currentHour >= 18 && currentHour < 19) startHour = 22;
  else if (currentHour >= 19 && currentHour < 20) startHour = 23;
  else return []; // 20시 이후는 예약 불가
  
  const endHour = 23;
  const timeSlots = [];
  
  for (let hour = startHour; hour <= endHour; hour++) {
    const hour12 = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    const ampm = hour >= 12 ? 'PM' : 'AM';
    timeSlots.push(`${hour12}:00 ${ampm}`);
  }
  
  return timeSlots;
};

const isScheduledDeliveryAvailable = () => {
  const currentKST = getCurrentKSTTime();
  const currentHour = currentKST.getHours();
  return currentHour < 20; // 20시 이전에만 예약배달 가능
};

const formatTimeForServer = (timeString: string) => {
  // "7:00 PM" 형식을 LocalDateTime으로 변환
  const [time, ampm] = timeString.split(' ');
  const [hourStr] = time.split(':');
  let hour = parseInt(hourStr);
  
  if (ampm === 'PM' && hour !== 12) hour += 12;
  if (ampm === 'AM' && hour === 12) hour = 0;
  
  // KST 기준으로 오늘 날짜 생성
  const today = getCurrentKSTTime();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  const hourStr24 = String(hour).padStart(2, '0');
  
  // LocalDateTime 형식으로 반환 (타임존 정보 없이)
  return `${year}-${month}-${day}T${hourStr24}:00:00`;
};

const CartPage: React.FC = () => {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { address } = useAddress();
  const {
    cart,
    updateItemQuantity,
    removeItem,
    restaurantMinOrderAmount,
    setRestaurantMinOrderAmount,
    getTotalPrice,
    getMinOrderAmount,
    isDeliveryAvailable,
    updateCartPricesForLanguage,
    fetchOrderId,
  } = useCart();
  const { t } = useTranslation();
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());
  
  // 로딩 상태 추가
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // 매장 영업 종료(폐업) 여부 - 주문 금액 계산 실패 사유가 SHOP_PERMANENTLY_CLOSED일 때 true
  const [isShopClosed, setIsShopClosed] = useState<boolean>(false);
  
  // 기념품 안내 모달 상태 추가
  const [showGiftShopModal, setShowGiftShopModal] = useState<boolean>(false);

  // 배달 시간 관련 상태 추가
  const [deliveryType, setDeliveryType] = useState<'immediate' | 'scheduled'>('immediate');
  const [selectedScheduledTime, setSelectedScheduledTime] = useState<string>('2:00 PM');
  
  // 예약배달 가능 여부 계산
  const isScheduledAvailable = useMemo(() => isScheduledDeliveryAvailable(), []);
  const availableTimeSlots = useMemo(() => getAvailableTimeSlots(), []);
  
  // UI 미노출: 예약배달 기능 일시적 미노출 중 - deliveryType을 항상 'immediate'로 고정
  // 예약배달이 불가능하거나 가능한 시간이 없으면 바로배달로 강제 설정
  useEffect(() => {
    // UI 미노출 중이므로 항상 immediate로 고정 (기능 제거가 아님)
    setDeliveryType('immediate');
    
    // 예약배달 관련 로직은 유지 (다시 노출 시 대비)
    // if (!isScheduledAvailable || availableTimeSlots.length === 0) {
    //   setDeliveryType('immediate');
    // } else if (availableTimeSlots.length > 0) {
    //   // 첫 번째 가능한 시간으로 기본값 설정
    //   setSelectedScheduledTime(availableTimeSlots[0]);
    // }
    
    // selectedScheduledTime 설정 로직은 유지 (다시 노출 시 대비)
    if (availableTimeSlots.length > 0) {
      setSelectedScheduledTime(availableTimeSlots[0]);
    }
  }, [isScheduledAvailable, availableTimeSlots]);

  // 주문 금액 계산 상태 추가
  const [orderCalculation, setOrderCalculation] = useState<CalculateOrderResponse | null>(null);
  const [isCalculating, setIsCalculating] = useState<boolean>(false);
  
  // 초기 로딩 상태 추가 (깜박임 방지용)
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);
  
  // 계산 중복 방지를 위한 ref
  const calculationInProgress = useRef<boolean>(false);
  
  // 마지막 계산된 시그니처를 저장하여 불필요한 재계산 방지
  const lastCalculatedSignature = useRef<string | null>(null);
  
  // API 응답 캐시를 위한 ref
  const calculationCache = useRef<Map<string, CalculateOrderResponse>>(new Map());

  // 장바구니 내용의 실제 변화만 감지하기 위한 시그니처 생성
  const cartSignature = useMemo(() => {
    if (!cart || !cart.items.length) return null;
    
    // 장바구니의 핵심 정보만으로 시그니처 생성
    return {
      restaurantId: cart.restaurantId,
      itemsCount: cart.items.length,
      itemsHash: cart.items.map(item => 
        `${item.id}-${item.quantity}-${item.options.map(opt => opt.id).join(',')}`
      ).join('|')
    };
  }, [cart]);

  // 디바운스된 시그니처 (500ms 지연)
  const debouncedCartSignature = useDebounce(cartSignature, 500);

  // 현재 언어에 맞는 통화 설정
  const [selectedCurrency, setSelectedCurrency] = useState<SupportedCurrency>(
    getCurrencyForCart(currentLang)
  );
  
  // 기프트샵 여부 확인
  const isGiftShop = useMemo(() => {
    return cart?.restaurantId === 'gift-shop-1';
  }, [cart?.restaurantId]);
  
  // 가게 정보 조회 및 최소 주문 금액 설정
  useEffect(() => {
    const fetchRestaurantDetails = async () => {
      // 장바구니가 비어있거나 레스토랑 ID가 없으면 실행하지 않음
      if (!cart || !cart.restaurantId) return;
      
      // 기프트샵은 가게 정보 조회를 건너뜀
      if (isGiftShop) {
        setIsInitialLoading(false);
        return;
      }
      
      // 이미 최소 주문 금액이 설정되어 있으면 실행하지 않음
      const currentMinOrderAmount = restaurantMinOrderAmount ? (restaurantMinOrderAmount.krw || restaurantMinOrderAmount.usd || restaurantMinOrderAmount.jpy || restaurantMinOrderAmount.cny || restaurantMinOrderAmount.twd || 0) : null;
      if (currentMinOrderAmount !== null) return;
      
      try {
        const restaurant = await getRestaurantDetails(String(cart.restaurantId));
        
        if (restaurant) {
          // 서버 응답에서 각 통화별 최소 주문 금액 가져오기
          const minOrderAmounts = {
            krw: restaurant.minOrderAmountKrw || restaurant.minOrderAmount || 0,
            usd: restaurant.minOrderAmountUsd || restaurant.minOrderAmount || 0,
            jpy: restaurant.minOrderAmountJpy || restaurant.minOrderAmount || 0,
            cny: restaurant.minOrderAmountCny || restaurant.minOrderAmount || 0,
            twd: restaurant.minOrderAmountTwd || restaurant.minOrderAmount || 0,
          };
          
          setRestaurantMinOrderAmount(minOrderAmounts);
        }
      } catch (error) {
        console.error('[CartPage] 가게 정보 조회 실패:', error);
      }
    };
    
    fetchRestaurantDetails();
  }, [cart, currentLang, restaurantMinOrderAmount, setRestaurantMinOrderAmount, isGiftShop]);
  
  // 현재 설정된 최소 주문 금액 로깅
  useEffect(() => {
    // amounts 변수 제거 - 사용되지 않음
  }, [restaurantMinOrderAmount, selectedCurrency]);
  
  // 언어 변경 핸들러 최적화
  const handleLanguageChange = useCallback(() => {
    const newLang = getCurrentLanguage();
    setCurrentLang(newLang);
    setSelectedCurrency(getCurrencyForCart(newLang));
    
    // CartContext의 가격 업데이트 함수 호출
    updateCartPricesForLanguage?.(newLang);
  }, [updateCartPricesForLanguage]);
  
  // 언어 변경 감지
  useEffect(() => {
    window.addEventListener('language-changed', handleLanguageChange);
    return () => {
      window.removeEventListener('language-changed', handleLanguageChange);
    };
  }, [handleLanguageChange]);
  
  // 주문 금액 계산 API 호출
  useEffect(() => {
    const calculateOrderAmount = async () => {
      // 장바구니가 비어있거나 주소가 없으면 실행하지 않음
      if (!cart || !cart.items.length || !address?.addressId || !debouncedCartSignature) {
        setIsInitialLoading(false); // 조건 불충족 시 초기 로딩 완료
        return;
      }
      
      // 시그니처 기반 캐시 키 생성 (기프트샵 여부도 포함)
      const cacheKey = `${JSON.stringify(debouncedCartSignature)}-${String(address.addressId)}-${currentLang}-${isGiftShop ? 'gift' : 'normal'}`;
      
      // 이미 같은 조건으로 계산했다면 건너뜀
      if (lastCalculatedSignature.current === cacheKey) {
        setIsInitialLoading(false);
        return;
      }
      
      // 캐시에서 결과 확인
      const cachedResult = calculationCache.current.get(cacheKey);
      if (cachedResult) {
        setOrderCalculation(cachedResult);
        lastCalculatedSignature.current = cacheKey;
        setIsInitialLoading(false);
        return;
      }
      
      // 이미 계산 중이면 중복 호출 방지
      if (calculationInProgress.current) {
        return;
      }
      
      try {
        calculationInProgress.current = true;
        setIsCalculating(true);
        // 새 계산 시도 시 이전 에러/폐업 상태 초기화
        setErrorMsg(null);
        setIsShopClosed(false);
        const promotionUserId = getPromotionUserId();
        
        // 기프트샵 여부에 따라 다른 변환 함수와 API 호출
        const calculation = isGiftShop 
          ? await calculateOrderGift(transformCartToCalculateGiftRequest(
              cart, 
              address.addressId || '', 
              currentLang.toUpperCase()
            ), promotionUserId)
          : await calculateOrder(transformCartToCalculateRequest(
              cart, 
              address.addressId || '', 
              currentLang.toUpperCase()
            ), promotionUserId);
        
        // 결과를 상태와 캐시에 저장
        setOrderCalculation(calculation);
        calculationCache.current.set(cacheKey, calculation);
        lastCalculatedSignature.current = cacheKey;
        
        // 캐시 크기 제한 (최대 10개 항목 유지)
        if (calculationCache.current.size > 10) {
          const firstKey = calculationCache.current.keys().next().value;
          if (firstKey) {
            calculationCache.current.delete(firstKey);
          }
        }
        
      } catch (error) {
        console.error('[CartPage] 주문 금액 계산 실패:', error);
        const apiError = error as { code?: string } | null;
        if (apiError?.code === SHOP_PERMANENTLY_CLOSED_CODE) {
          // 폐업 매장: 전용 안내 카드로 처리하고 결제를 차단 (빨간 일반 에러로 노출하지 않음)
          setIsShopClosed(true);
          setErrorMsg(null);
        } else {
          // 그 외 계산 실패: 사용자 언어로 일반 안내 (하드코딩 한국어 제거)
          setErrorMsg(t("cart.calculateFailed"));
        }
      } finally {
        calculationInProgress.current = false;
        setIsCalculating(false);
        setIsInitialLoading(false); // API 완료 후 초기 로딩 완료
      }
    };
    
    // 디바운스가 적용되어 있으므로 즉시 실행
    calculateOrderAmount();
  }, [
    cart,
    address?.addressId,
    currentLang,
    debouncedCartSignature,
    isGiftShop
  ]);
  
  // 소계, 배송비, 총액을 API 응답에서만 가져오기 (클라이언트 계산 완전 제거)
  const subtotal = useMemo(() => {
    if (!orderCalculation) {
      return getTotalPrice();
    }

    // 소계는 프로모션 영향을 받지 않음 (배달비 프로모션)
    switch(selectedCurrency) {
      case 'USD': return orderCalculation.subtotalUsd;
      case 'JPY': return orderCalculation.subtotalJpy;
      case 'CNY': return orderCalculation.subtotalCny;
      case 'TWD': return orderCalculation.subtotalTwd;
      case 'KRW':
      default: return orderCalculation.subtotalKrw;
    }
  }, [orderCalculation, getTotalPrice, selectedCurrency, currentLang]);

  // 프로모션 활성화 여부 확인
  const isPromotionActive = useMemo(() => {
    if (!orderCalculation) return false;

    switch(selectedCurrency) {
      case 'USD': return orderCalculation.isPromotionUsd;
      case 'JPY': return orderCalculation.isPromotionJpy;
      case 'CNY': return orderCalculation.isPromotionCny;
      case 'TWD': return orderCalculation.isPromotionTwd;
      case 'KRW':
      default: return false; // KRW는 프로모션 필드가 없으므로 false
    }
  }, [orderCalculation, selectedCurrency, currentLang]);

  const deliveryFee = useMemo(() => {
    if (!orderCalculation) return 0; // API 응답이 없으면 0
    
    // 프로모션 활성화 여부에 따라 할인된 배송비 또는 원래 배송비 반환
    if (isPromotionActive) {
      switch(selectedCurrency) {
        case 'USD': return orderCalculation.promotionDeliveriesUsd;
        case 'JPY': return orderCalculation.promotionDeliveriesJpy;
        case 'CNY': return orderCalculation.promotionDeliveriesCny;
        case 'TWD': return orderCalculation.promotionDeliveriesTwd;
        case 'KRW':
        default: return orderCalculation.deliveryFeeKrw; // KRW는 프로모션 배달비 필드가 없으므로 원래 배달비 사용
      }
    } else {
      // 프로모션이 비활성화된 경우 원래 배송비
      switch(selectedCurrency) {
        case 'USD': return orderCalculation.deliveryFeeUsd;
        case 'JPY': return orderCalculation.deliveryFeeJpy;
        case 'CNY': return orderCalculation.deliveryFeeCny;
        case 'TWD': return orderCalculation.deliveryFeeTwd;
        case 'KRW':
        default: return orderCalculation.deliveryFeeKrw;
      }
    }
  }, [orderCalculation, selectedCurrency, isPromotionActive, currentLang]);

  const total = useMemo(() => {
    if (!orderCalculation) {
      return subtotal + deliveryFee;
    }
    
    // 프로모션 활성화 여부 확인 및 프로모션 총액 사용
    if (isPromotionActive) {
      switch(selectedCurrency) {
        case 'USD': return orderCalculation.promotionTotalPriceUsd;
        case 'JPY': return orderCalculation.promotionTotalPriceJpy;
        case 'CNY': return orderCalculation.promotionTotalPriceCny;
        case 'TWD': return orderCalculation.promotionTotalPriceTwd;
        case 'KRW':
        default: return orderCalculation.totalPriceKrw; // KRW는 프로모션 총액 필드가 없으므로 원래 총액 사용
      }
    } else {
      // 프로모션이 비활성화된 경우 원래 총액
      switch(selectedCurrency) {
        case 'USD': return orderCalculation.totalPriceUsd;
        case 'JPY': return orderCalculation.totalPriceJpy;
        case 'CNY': return orderCalculation.totalPriceCny;
        case 'TWD': return orderCalculation.totalPriceTwd;
        case 'KRW':
        default: return orderCalculation.totalPriceKrw;
      }
    }
  }, [orderCalculation, subtotal, deliveryFee, selectedCurrency, isPromotionActive, currentLang]);

  const promotionResolution = useMemo(
    () => getPromotionResolution(orderCalculation),
    [orderCalculation]
  );

  useEffect(() => {
    if (!orderCalculation) return;
    if (process.env.NODE_ENV !== 'development') return;
    console.info('[CartPage] Promotion resolution', promotionResolution);
  }, [orderCalculation, promotionResolution]);
  
  // 원래 배달비 (프로모션 적용 전)
  const originalDeliveryFee = useMemo(() => {
    if (!orderCalculation) return 0;

    switch(selectedCurrency) {
      case 'USD': return orderCalculation.deliveryFeeUsd;
      case 'JPY': return orderCalculation.deliveryFeeJpy;
      case 'CNY': return orderCalculation.deliveryFeeCny;
      case 'TWD': return orderCalculation.deliveryFeeTwd;
      case 'KRW':
      default: return orderCalculation.deliveryFeeKrw;
    }
  }, [orderCalculation, selectedCurrency, currentLang]);

  // 프로모션 할인율 계산 (배달비 기준)
  const promotionDiscountRate = useMemo(() => {
    if (!orderCalculation || !isPromotionActive) return 0;
    
    const promotionRatio = orderCalculation.promotionRatio || 0;
    return Math.round(promotionRatio * 100); // 백분율로 변환
  }, [orderCalculation, isPromotionActive]);

  // 최소 주문 금액 (CartContext fallback 용 - 구버전 서버 호환)
  const minOrderAmount = useMemo(() => {
    return getMinOrderAmount();
  }, [getMinOrderAmount]);

  // 실효 최소 주문 금액 (calculate-order 응답 우선, 없으면 CartContext fallback)
  // 표시 통화(selectedCurrency) 기준 필드를 그대로 사용 - 구버전 서버 호환을 위해 minOrderAmount로 fallback
  const effectiveMinOrderAmount = useMemo<number | null>(() => {
    if (!orderCalculation) return minOrderAmount;

    switch (selectedCurrency) {
      case 'USD': return orderCalculation.minOrderAmountUsd ?? orderCalculation.minOrderAmount ?? minOrderAmount;
      case 'JPY': return orderCalculation.minOrderAmountJpy ?? orderCalculation.minOrderAmount ?? minOrderAmount;
      case 'CNY': return orderCalculation.minOrderAmountCny ?? orderCalculation.minOrderAmount ?? minOrderAmount;
      case 'TWD': return orderCalculation.minOrderAmountTwd ?? orderCalculation.minOrderAmount ?? minOrderAmount;
      case 'KRW':
      default:
        return orderCalculation.minOrderAmountKrw ?? orderCalculation.minOrderAmount ?? minOrderAmount;
    }
  }, [orderCalculation, selectedCurrency, minOrderAmount]);

  // 최소 주문 금액 충족 여부
  // 서버의 isMinOrderAmountMet(옵션·배송비 제외 상품 소계 기준)를 우선 신뢰하고,
  // 구버전 서버 호환을 위해 값이 없으면 클라이언트 계산으로 fallback
  const deliveryAvailable = useMemo(() => {
    if (orderCalculation && typeof orderCalculation.isMinOrderAmountMet === 'boolean') {
      return orderCalculation.isMinOrderAmountMet;
    }
    return isDeliveryAvailable(minOrderAmount);
  }, [orderCalculation, isDeliveryAvailable, minOrderAmount]);

  // 표시용 부족분 = 실효 최소금액 - 상품 소계 (최종 차단 여부는 deliveryAvailable로 판단)
  const amountToMinOrder = useMemo(() => {
    if (effectiveMinOrderAmount == null) return 0;
    const diff = effectiveMinOrderAmount - subtotal;
    return diff > 0 ? diff : 0;
  }, [effectiveMinOrderAmount, subtotal]);

  // 가격 포맷팅 및 현재 통화에 맞는 가격 반환
  const formatPrice = (price: number) => {
    return formatPriceWithCurrency(price, selectedCurrency);
  };
  
  // 가게 상세 페이지로 이동
  const navigateToRestaurant = () => {
    if (cart && cart.restaurantId) {
      navigate(`/restaurant/${cart.restaurantId}`, { replace: false });
    }
  };

  // 레스토랑 브라우징 페이지로 이동
  const navigateToRestaurants = () => {
    navigate("/restaurants", { replace: false }); // 음식점 목록 페이지로 이동
  };

  // 수량 증가 (useCallback으로 최적화)
  const increaseQuantity = useCallback((itemId: string, currentQuantity: number) => {
    updateItemQuantity(itemId, currentQuantity + 1);
  }, [updateItemQuantity]);

  // 수량 감소 (useCallback으로 최적화)
  const decreaseQuantity = useCallback((itemId: string, currentQuantity: number) => {
    if (currentQuantity > 1) {
      updateItemQuantity(itemId, currentQuantity - 1);
    } else {
      removeItem(itemId);
    }
  }, [updateItemQuantity, removeItem]);

  // 메뉴 삭제 (useCallback으로 최적화)
  const handleRemoveItem = useCallback((itemId: string) => {
    removeItem(itemId);
  }, [removeItem]);

  // 체크아웃 에러 메시지 결정 (최소주문금액 미달 코드는 기존과 동일한 안내 문구로 변환)
  const resolveCheckoutErrorMessage = useCallback(
    (error: unknown): string => {
      const apiError = error as { code?: string; message?: string } | null;
      if (apiError?.code === MIN_ORDER_AMOUNT_NOT_MET_CODE) {
        // 백엔드 400(MIN_ORDER_AMOUNT_NOT_MET) → 기존 최소주문 경고 문구로 처리(앱 깨짐 방지)
        return t("cart.minOrderWarning");
      }
      if (apiError?.code === SHOP_PERMANENTLY_CLOSED_CODE) {
        // 폐업 매장 → 백엔드 한국어 메시지 대신 사용자 언어 안내
        return t("cart.shopClosedTitle");
      }
      if (error instanceof Error) return error.message;
      if (apiError?.message) return apiError.message;
      return t("cart.checkoutFailed");
    },
    [t]
  );

  // 체크아웃 진행
  const handleCheckout = async () => {
    if (!cart) return;

    // 폐업 매장은 주문 불가
    if (isShopClosed) return;

    // 배달 시간 유효성 검증
    if (deliveryType === 'scheduled') {
      if (!isScheduledAvailable || availableTimeSlots.length === 0) {
        // 예약배달이 불가능한 상황이면 바로배달로 자동 변경
        setDeliveryType('immediate');
        setErrorMsg(t("cart.changedToImmediateDelivery"));
        
        // 3초 후 메시지 자동 삭제
        setTimeout(() => {
          setErrorMsg(null);
        }, 3000);
        
        return; // 첫 번째 체크아웃 시도는 여기서 중단, 사용자가 다시 버튼을 눌러야 함
      }
    }

    // 기념품 상품일 때 안내 모달 표시
    if (isGiftShop) {
      setShowGiftShopModal(true);
      return;
    }

    // 최소 주문 금액이 충족되지 않았을 때 알림 표시 (서버 isMinOrderAmountMet 기준)
    if (!deliveryAvailable) {
      // 경고 메시지 표시 (기존과 동일한 문구)
      setErrorMsg(t("cart.minOrderWarning"));
      
      // 3초 후 메시지 자동 삭제
      setTimeout(() => {
        setErrorMsg(null);
      }, 3000);
      
      return; // 여기서 함수 종료됨
    }

    try {
      // 로딩 상태 설정
      setIsLoading(true);
      setErrorMsg(null);

      // 주소 ID 가져오기
      const addressId = address?.addressId || ""; // 저장된 주소 ID 또는 임시 ID 사용

      if (!address?.isComplete) {
        setErrorMsg(t("cart.setAddressFirst"));
        setIsLoading(false);
        navigate({
          pathname: "/address",
          search: routerLocation.search,
        });
        return;
      }

      if (!addressId) {
        console.warn("Address ID is missing. Using temporary ID.");
      }

      // CartContext의 fetchOrderId 사용 (기념품 여부에 따라 자동으로 적절한 API 호출)
      const reservationDateTime = deliveryType === 'scheduled' ? formatTimeForServer(selectedScheduledTime) : undefined;
      const originStayId = address?.stay?.stay_id ?? address?.stay?.id;
      const cartResponse = await fetchOrderId(addressId, reservationDateTime, originStayId);

      if (!cartResponse || !cartResponse.orderId) {
        throw new Error(t("cart.failedToCreateCart"));
      }

      // 주문 ID를 사용하여 결제 페이지로 이동 (pass selected currency)
      navigate(
        `/checkout?orderId=${cartResponse.orderId}&currency=${selectedCurrency}`,
      );
    } catch (error) {
      // 에러 처리 - 방어적 처리: 백엔드가 최소주문금액 미달로 차단한 경우 친화 메시지로 안내
      console.error("Checkout error:", error);
      setErrorMsg(resolveCheckoutErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  // 기념품 체크아웃 진행
  const handleGiftShopCheckout = async () => {
    setShowGiftShopModal(false);
    
    try {
      // 로딩 상태 설정
      setIsLoading(true);
      setErrorMsg(null);

      // 주소 ID 가져오기
      const addressId = address?.addressId || "";

      if (!address?.isComplete) {
        setErrorMsg(t("cart.setAddressFirst"));
        setIsLoading(false);
        navigate({
          pathname: "/address",
          search: routerLocation.search,
        });
        return;
      }

      // CartContext의 fetchOrderId 사용 (기념품 API 호출)
      const reservationDateTime = deliveryType === 'scheduled' ? formatTimeForServer(selectedScheduledTime) : undefined;
      const originStayId = address?.stay?.stay_id ?? address?.stay?.id;
      const cartResponse = await fetchOrderId(addressId, reservationDateTime, originStayId);

      if (!cartResponse || !cartResponse.orderId) {
        throw new Error(t("cart.failedToCreateCart"));
      }

      // 주문 ID를 사용하여 결제 페이지로 이동
      navigate(
        `/checkout?orderId=${cartResponse.orderId}&currency=${selectedCurrency}`,
      );
    } catch (error) {
      // 에러 처리 - 방어적 처리: 백엔드가 최소주문금액 미달로 차단한 경우 친화 메시지로 안내
      console.error("Gift shop checkout error:", error);
      setErrorMsg(resolveCheckoutErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  // 카트가 비어있을 때
  if (!cart || cart.items.length === 0) {
    return (
      <>
        <div data-figma-capture-root className="min-h-screen bg-gray-50">
          <BackHeader title={t("nav.cart")} />
          <div className="flex flex-col items-center justify-center h-[calc(100vh-120px)] px-6 text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-3">
              {t("cart.empty")}
            </h2>
            {/* <p className="text-gray-500 mb-8">{t("cart.addItems")}</p> */}

            <p className="text-gray-500 mb-8"></p>
            <button
              onClick={navigateToRestaurants}
              className="bg-red-500 text-white py-3 px-6 rounded-md font-medium hover:bg-red-600 transition-colors"
            >
              {t("cart.browseRestaurants")}
            </button>
          </div>
        </div>
        <NavigationBar />
      </>
    );
  }

  // 계산 중일 때 스켈레톤 표시
  if (isCalculating || isInitialLoading) {
    return <CartPageSkeleton />;
  }

  return (
    <>
      <div data-figma-capture-root className="min-h-screen bg-gray-50">
        {/* GNB */}
        <BackHeader title={t("nav.cart")} />

        <main className="px-4 pb-[128px]">
        {/* 가게 정보 */}
        <div
          className="py-4 bg-white mb-4 border-b border-gray-200 cursor-pointer -mx-4 px-4"
          onClick={navigateToRestaurant}
        >
          <h2 className="text-lg font-medium text-left flex items-center text-gray-800">
            {/* 
              현재 언어에 맞는 가게 이름 표시, 없으면 영어 이름, 그것도 없으면 기본 이름 사용
            */}
            {(() => {
              // 이름 표시 형식 (말줄임표 처리)
              const formatName = (name: string): string => {
                if (!name || typeof name !== 'string') return 'Unknown Restaurant';
                return name.length > 20 ? `${name.substring(0, 18)}...` : name;
              };
              
              // 1. 현재 언어에 맞는 이름 필드 선택
              let displayName: string | undefined;
              
              // 언어별 필드 이름
              if (currentLang === 'en' && cart.restaurantNameEn) {
                displayName = cart.restaurantNameEn;
              } else if (currentLang === 'ja' && cart.restaurantNameJa) {
                displayName = cart.restaurantNameJa;
              } else if (currentLang === 'zh-CN' && cart.restaurantNameZhCn) {
                displayName = cart.restaurantNameZhCn;
              } else if (currentLang === 'zh-TW' && cart.restaurantNameZhTw) {
                displayName = cart.restaurantNameZhTw;
              } else if (currentLang === 'ko' && cart.restaurantNameKo) {
                displayName = cart.restaurantNameKo;
              }
              
              // 현재 언어에 맞는 이름이 없으면 영어 이름, 그것도 없으면 기본 이름 사용
              return formatName(displayName || cart.restaurantNameEn || cart.restaurantName);
            })()}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="w-5 h-5 ml-1 text-gray-500"
            >
              <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
            </svg>
          </h2>
        </div>

        {/* 중국어 사용자를 위한 통화 변환 안내 */}
        {currentLang === 'zh-CN' && (
          <div className="bg-yellow-300 border border-yellow-200 rounded-lg p-4 mb-4 -mx-4 mx-4">
            <div className="flex items-start">
              <div className="flex-1">
                <p className="text-yellow-800 font-medium text-xs mb-1">
                ‼️ 技术原因暂不支持人民币，将自动转为美元结算。
                </p>
                <p className="text-yellow-800 font-medium text-xs mb-1">
                  🙇🏻 给您带来不便，敬请谅解，我们正在优化支付方式。
                  </p>
              </div>
            </div>
          </div>
        )}

        {/* 카트 아이템 목록 */}
        <div className="divide-y divide-gray-200">
          {cart.items.map((item) => (
            <CartItem
              key={item.id}
              item={item}
              currentLang={currentLang}
              selectedCurrency={selectedCurrency}
              onIncrease={increaseQuantity}
              onDecrease={decreaseQuantity}
              onRemove={handleRemoveItem}
            />
          ))}
        </div>


        {/* UI 미노출: 배달 도착 시간 섹션 - 예약주문 기능 일시적 미노출 */}
        {/* 배달 도착 시간 */}
        {/* 
        <div
          className="bg-white p-4 rounded-lg shadow-sm mb-4"
          data-promotion-source={promotionResolution.source}
          data-applied-promotion-id={promotionResolution.appliedPromotionId || ''}
          data-applied-policy-id={promotionResolution.appliedPolicyId || ''}
        >
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-lg font-medium text-gray-800">
              {t("cart.deliveryTime")}
            </h3>
            <span className="text-sm text-gray-600">
              {deliveryType === 'immediate' 
                ? t('restaurant.deliveryTime')
                : selectedScheduledTime
              }
            </span>
          </div>
          
          <div className="space-y-3">
            바로배달 옵션
            <div 
              onClick={() => setDeliveryType('immediate')}
              className={`flex items-center p-3 rounded-lg border cursor-pointer transition-all duration-150 ${
                deliveryType === 'immediate' 
                  ? 'border-2 border-red-500 bg-red-50' 
                  : 'border border-gray-200 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center space-x-3 flex-1">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  deliveryType === 'immediate' 
                    ? 'border-red-500 bg-red-500' 
                    : 'border-gray-300'
                }`}>
                  {deliveryType === 'immediate' && (
                    <div className="w-2 h-2 rounded-full bg-white"></div>
                  )}
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">
                  ⚡&nbsp;{t("badge.orderNow")}
                  </h4>
                </div>
              </div>
            </div>

            예약배달 옵션
            <div 
              onClick={() => {
                if (isScheduledAvailable && availableTimeSlots.length > 0) {
                  setDeliveryType('scheduled');
                  setShowDeliveryTimeModal(true);
                }
              }}
              className={`flex items-center p-3 rounded-lg border transition-all duration-150 ${
                deliveryType === 'scheduled' 
                  ? 'border-2 border-red-500 bg-red-50' 
                  : isScheduledAvailable && availableTimeSlots.length > 0
                  ? 'border border-gray-200 hover:bg-gray-50 cursor-pointer'
                  : 'border border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
              }`}
            >
              <div className="flex items-center space-x-3 flex-1">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  deliveryType === 'scheduled' 
                    ? 'border-red-500 bg-red-500' 
                    : isScheduledAvailable && availableTimeSlots.length > 0
                    ? 'border-gray-300'
                    : 'border-gray-300 bg-gray-200'
                }`}>
                  {deliveryType === 'scheduled' && (
                    <div className="w-2 h-2 rounded-full bg-white"></div>
                  )}
                </div>
                <div className="flex-1">
                  <h4 className={`font-medium ${
                    isScheduledAvailable && availableTimeSlots.length > 0
                      ? 'text-gray-900'
                      : 'text-gray-500'
                  }`}>
                    🕐 &nbsp;{t("badge.orderLater")}
                  </h4>
                  {(!isScheduledAvailable || availableTimeSlots.length === 0) && (
                    <p className="text-xs text-gray-400 mt-1">
                      {t('cart.scheduledDeliveryNotAvailable')}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        */}

        {/* 가격 요약 */}
        <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
          <h3 className="text-lg font-medium mb-3 text-gray-800 text-left">
            {t("cart.orderSummary")}
          </h3>
          <div className="space-y-2 text-left">
            <div className="flex justify-between">
              <span className="text-gray-600">{t("cart.subtotal")}</span>
              <div className="text-right">
                {isInitialLoading ? (
                  <div className="h-5 w-16 bg-gray-200 rounded animate-pulse"></div>
                ) : (
                  <>
                    {currentLang === 'zh-CN' ? (
                      <div className="space-y-1">
                        <div className="font-medium">
                          {formatPriceWithCurrency(convertCurrency(subtotal, 'USD', 'CNY'), 'CNY')}
                        </div>
                      </div>
                    ) : (
                      <span className="font-medium">
                        {formatPrice(subtotal)}
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">{t("cart.deliveryFee")}</span>
              <span className="font-medium">
                {isInitialLoading ? (
                  <div className="h-5 w-16 bg-gray-200 rounded animate-pulse"></div>
                ) : (
                  <>
                    {isPromotionActive && (
                      <div className="text-sm text-gray-400 line-through text-right">
                        {currentLang === 'zh-CN' ? (
                          <div className="space-y-1">
                            <div>{formatPriceWithCurrency(convertCurrency(originalDeliveryFee, 'USD', 'CNY'), 'CNY')}</div>
                          </div>
                        ) : (
                          formatPrice(originalDeliveryFee)
                        )}
                      </div>
                    )}
                    <div className="flex items-center space-x-2">
                      {isPromotionActive && promotionDiscountRate > 0 && (
                        <span className="text-red-500 font-bold text-sm">
                          {promotionDiscountRate === 100 ? `${t('common.free')}` : `${promotionDiscountRate}%`}
                        </span>
                      )}
                      {currentLang === 'zh-CN' ? (
                        <div className="text-right space-y-1">
                          <div className="font-medium">
                            {formatPriceWithCurrency(convertCurrency(deliveryFee, 'USD', 'CNY'), 'CNY')}
                          </div>
                        </div>
                      ) : (
                        <span className={`font-medium`}>
                          {formatPrice(deliveryFee)}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </span>
            </div>
            <div className="border-t pt-2 mt-2 flex justify-between">
              <span className="font-medium text-gray-800">
                {t("cart.total")}
              </span>
              <div className="text-right">
                {isInitialLoading ? (
                  <div className="h-6 w-20 bg-gray-200 rounded animate-pulse"></div>
                ) : (
                  <>
                    {currentLang === 'zh-CN' ? (
                      <div className="space-y-1">
                        <div className="text-gray-600">
                          {formatPriceWithCurrency(convertCurrency(total, 'USD', 'CNY'), 'CNY')}
                        </div>
                        <div className="font-bold text-xl">
                          ≈ {formatPrice(total)}
                        </div>
                      </div>
                    ) : (
                      <span className={`font-bold text-xl`}>
                        {formatPrice(total)}
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>


        {/* 배달 최소 주문 금액 안내 (미충족 상태일 때만) */}
        {!deliveryAvailable && !isGiftShop && (
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 mb-4 text-left">
            <div className="flex items-start">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-yellow-500 mt-0.5 mr-2 flex-shrink-0"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <p className="text-yellow-700 font-medium">
                  {t("cart.minOrderWarning")}
                </p>
                <p className="text-yellow-600 text-sm mt-1">
                  {t("cart.needMore", { amount: formatPrice(amountToMinOrder) })}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* 매장 영업 종료(폐업) 안내 */}
        {isShopClosed && (
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-4 text-left">
            <div className="flex items-start">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-gray-500 mt-0.5 mr-2 flex-shrink-0"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <p className="text-gray-800 font-medium">
                  {t("cart.shopClosedTitle")}
                </p>
                <p className="text-gray-600 text-sm mt-1">
                  {t("cart.shopClosedDescription")}
                </p>
                <button
                  onClick={navigateToRestaurants}
                  className="mt-3 bg-red-500 text-white py-2 px-4 rounded-md text-sm font-medium hover:bg-red-600 transition-colors"
                >
                  {t("cart.browseOtherRestaurants")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 에러 메시지 */}
        {errorMsg && (
          <div className="bg-red-50 p-4 rounded-lg border border-red-200 mb-4 text-left">
            <div className="flex items-start">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-red-500 mt-0.5 mr-2 flex-shrink-0"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <p className="text-red-700">{errorMsg}</p>
            </div>
          </div>
        )}

        {/* 중국어 사용자를 위한 환율 고지 */}
        {currentLang === 'zh-CN' && orderCalculation && (
          <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 mb-4 text-center">
            <p className="text-gray-600 text-sm">
            * 汇率参考：¥1 ≈ ${orderCalculation.exchangeRateUsd && orderCalculation.exchangeRateCny ? (orderCalculation.exchangeRateUsd / orderCalculation.exchangeRateCny).toFixed(2) : '0.14'}（每日更新一次）
            </p>
          </div>
        )}
        </main>

        {/* 결제 버튼 영역 */}
        <div className="figma-capture-cart-footer fixed bottom-[70px] left-1/2 transform -translate-x-1/2 bg-white shadow-top p-4 z-20 max-w-mobile w-full">
        <div className="w-full">
          <button
            onClick={handleCheckout}
            disabled={isLoading || isInitialLoading || isShopClosed || (!deliveryAvailable && !isGiftShop)}
            className={`w-full py-3 px-4 rounded-md font-semibold text-white flex justify-center items-center ${
              isShopClosed
                ? "bg-gray-400 cursor-not-allowed"
                : !isLoading && !isInitialLoading && (deliveryAvailable || isGiftShop)
                ? "bg-red-500 hover:bg-red-600"
                : !isLoading && !isInitialLoading && !deliveryAvailable && !isGiftShop
                ? "bg-yellow-500 hover:bg-yellow-600"
                : "bg-gray-400 cursor-not-allowed"
            }`}
          >
            {isLoading || isInitialLoading ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                {isInitialLoading ? t("cart.loading") : t("cart.processing")}
              </>
            ) : isShopClosed ? (
              t("cart.shopClosedTitle")
            ) : !deliveryAvailable && !isGiftShop ? (
              t("cart.minOrderWarning")
            ) : (
              t("cart.proceedToCheckout")
            )}
          </button>
        </div>
        </div>
      </div>

      {/* 네비게이션 바 */}
      <NavigationBar />

      {/* 기념품 안내 모달 */}
      {showGiftShopModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg md-elevation-24 max-w-md w-full mx-4 md-smooth-transition max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="px-6 pt-6 pb-4">
              <div className="flex items-start">
                <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                  <svg className="w-4 h-4 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="md-title-medium text-gray-900 font-medium leading-tight">
                  {t('cart.giftShopModalTitle')}
                </h3>
              </div>
            </div>
            
            {/* Modal Content */}
            <div className="px-6 pb-6">
              {/* Policy Details */}
              <div className="border border-gray-200 rounded-md p-4 mb-6 bg-gray-50">
                <div className="space-y-2 text-left">
                  {t('cart.giftShopModalDetails').split('\n').map((line, index) => (
                    <p key={index} className="md-body-small text-gray-700 leading-relaxed text-left">
                      <span dangerouslySetInnerHTML={{ 
                        __html: line.replace(/\*\*(.*?)\*\*/g, '<strong class="text-red-600 font-semibold">$1</strong>') 
                      }} />
                    </p>
                  ))}
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex flex-col gap-3">
                <button
                  onClick={handleGiftShopCheckout}
                  className="md-button-filled w-full py-3 px-6 bg-red-500 text-white rounded-md font-medium hover:bg-red-600 active:bg-red-700 md-elevation-2 hover:md-elevation-4 transition-all duration-200 md-smooth-transition focus:outline-none focus:ring-2 focus:ring-red-300 focus:ring-offset-2"
                >
                  {t('cart.giftShopModalContinue')}
                </button>
                <button
                  onClick={() => setShowGiftShopModal(false)}
                  className="md-button-text w-full py-2.5 px-6 rounded-md text-gray-700 font-medium hover:bg-gray-50 active:bg-gray-100 transition-colors duration-200 md-smooth-transition focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2"
                >
                  {t('cart.giftShopModalCancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* UI 미노출: 배달 시간 선택 바텀시트 - 예약주문 기능 일시적 미노출 */}
      {/* 배달 시간 선택 바텀시트 */}
      {/* 
      {showDeliveryTimeModal && (
        <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50 p-0 ${
          isClosingModal ? 'animate-fade-out' : 'animate-fade-in'
        }`} onClick={handleCloseModal}>
          <div className={`bg-white rounded-t-lg w-full max-w-mobile mx-auto max-h-[60vh] overflow-y-auto ${
            isClosingModal ? 'animate-slide-down' : 'animate-slide-up'
          }`} onClick={(e) => e.stopPropagation()}>
            바텀시트 헤더
            <div className="px-6 pt-6 pb-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  {t('cart.deliveryTimeSelection')}
                </h3>
                <button
                  onClick={handleCloseModal}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <svg className="w-5 h-5 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>

            시간 선택 리스트
            <div className="px-6 py-4">
              <div className="space-y-3">
                {availableTimeSlots.map((time) => (
                  <button
                    key={time}
                    onClick={() => {
                      setSelectedScheduledTime(time);
                      handleCloseModal();
                    }}
                    className={`w-full p-4 text-center rounded-lg border transition-all duration-150 ${
                      selectedScheduledTime === time
                        ? 'border-red-500 bg-red-50 text-red-700'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <span className="font-medium">{time}</span>
                  </button>
                ))}
              </div>
            </div>

            확인 버튼
            <div className="px-6 pb-6">
              <button
                onClick={handleCloseModal}
                className="w-full py-3 px-6 text-white rounded-lg font-medium hover:bg-red-600 active:bg-red-700 transition-all duration-200"
              >
                {t('cart.selectTime')}
              </button>
            </div>
          </div>
        </div>
      )}
      */}
    </>
  );
};

export default CartPage;
